import React, { useState, useMemo, useEffect } from 'react'
import { Form, Button, Row, Col, Spinner as BSSpinner } from 'react-bootstrap'
import Swal from 'sweetalert2'
import { actualizarDatos, addEquipments, addEquiposProgramacion, getDataList, POST } from '../../api/common'
import { Alertassw } from '../Alertas/Alertassw'
import EquipoManager from './CrearOt/EquipoManager'
import ListaEquiposConfirmacion from './CrearOt/ListaEquiposConfirmacion'
import ModalDuplicados from './CrearOt/ModalDuplicados'
import ModalResolverItems from './CrearOt/ModalResolverItems'
import { PARENT } from '../../constants'

/**
 * AgregarEquiposOt — Replaces the old AddItems component in Otselect.
 *
 * Props:
 *   ot              — full OT object from Otselect (ots state)
 *   setEstadoModal  — close the parent Modales wrapper
 *   setRefresh      — trigger OT data reload in parent
 *   refresh         — current refresh toggle value
 *   sedes           — sedes list for the client (from Cabecera setSedes)
 *   sedeSeleted     — currently selected sede filter
 *   historialeq     — { clienteId, data } cache stored in Otselect state
 *   setHistorialEq  — setter to persist cache back into Otselect state
 */
const AgregarEquiposOt = ({
  ot,
  setEstadoModal,
  setRefresh,
  refresh,
  sedes = [],
  sedeSeleted,
  historialeq,
  setHistorialEq,
}) => {
  // ── Equipos pendientes de guardar ─────────────────────────────────
  const [pendingEquipos, setPendingEquipos] = useState([])
  const [motivoAdicion, setMotivoAdicion] = useState('')
  const [savingEquipos, setSavingEquipos] = useState(false)

  // ── Sedes del cliente (todas, no solo las que están en la OT) ─────
  const [clienteSedes, setClienteSedes] = useState([])
  const [sedeIdEnModal, setSedeIdEnModal] = useState(sedeSeleted || '')
  const [cargandoSedes, setCargandoSedes] = useState(false)

  // ── Modales ───────────────────────────────────────────────────────
  const [modalDuplicados, setModalDuplicados] = useState({
    show: false, data: null, equiposYaAgregados: [], onSelect: null, onHide: null,
  })
  const [modalResolverItems, setModalResolverItems] = useState({
    show: false, data: null, onResolve: null, onHide: null,
  })

  // ── Computed values ───────────────────────────────────────────────
  const clienteId = ot?.cliente?._id || ot?.cliente

  const equiposEnOtIds = useMemo(
    () => (ot?.equipos || []).map(e => ({ _id: e.equipo?._id || e.equipo })),
    [ot?.equipos]
  )

  const todosEquiposAgregados = useMemo(
    () => [...pendingEquipos, ...equiposEnOtIds],
    [pendingEquipos, equiposEnOtIds]
  )

  
  // ── Cargar TODAS las sedes del cliente para el select del modal ───
  useEffect(() => {
    if (!clienteId) return
    setCargandoSedes(true)
    getDataList('Sedes', { conditions: { cliente: clienteId } })
      .then(res => {
        const lista = res?.data?.data || []
        setClienteSedes(lista)
        // Default: si no hay sede seleccionada en la cabecera, usar la primera sede del cliente
        // (la más antigua por createdAt = sede principal por convención)
        if (!sedeSeleted && lista.length > 0) {
          const ordenadas = [...lista].sort((a, b) => {
            const da = new Date(a.createdAt || 0).getTime()
            const db = new Date(b.createdAt || 0).getTime()
            return da - db
          })
          setSedeIdEnModal(ordenadas[0]._id)
        } else if (sedeSeleted) {
          setSedeIdEnModal(sedeSeleted)
        }
      })
      .catch(err => console.error('[AgregarEquiposOt] Error cargando sedes del cliente:', err))
      .finally(() => setCargandoSedes(false))
  }, [clienteId, sedeSeleted])

  // ── Cache de equipos del cliente ──────────────────────────────────
  // If the cached entry belongs to this client, reuse it; otherwise fetch fresh.
  const initialEquipos = historialeq && historialeq.clienteId === clienteId ? historialeq.data : null

  // Cache paralelo de últimas calibraciones (tab "Últimas Cal.").
  // Mismo objeto historialeq, extendido con { ultimasCal, resumen }.
  const initialDataUltimasCal = historialeq && historialeq.clienteId === clienteId && historialeq.ultimasCal
    ? { clienteId, data: historialeq.ultimasCal, resumen: historialeq.resumen }
    : null

  // Spread del estado previo cuando el clienteId coincide, para que la carga
  // de un tab no pise la caché del otro. Al cambiar de cliente, se reinicia limpio.
  const handleEquiposLoaded = (data) => {
    setHistorialEq?.(prev => ({
      ...(prev?.clienteId === clienteId ? prev : {}),
      clienteId,
      data,
    }))
  }

  const handleUltimasCalLoaded = (ultimasCal, resumen) => {
    setHistorialEq?.(prev => ({
      ...(prev?.clienteId === clienteId ? prev : {}),
      clienteId,
      ultimasCal,
      resumen,
    }))
  }

  // ── Validar duplicados ────────────────────────────────────────────
  const validarDuplicados = async (series) => {
    if (!clienteId) return { cancelled: true, nuevosData: [], duplicadosData: [] }
    try {
      const response = await POST('/Equipos/validar-duplicados', { series, clienteId })
      const { nuevos, duplicados } = response.data
      if (duplicados.length > 0) {
        return new Promise((resolve) => {
          setModalDuplicados({
            show: true,
            data: { nuevos, duplicados },
            equiposYaAgregados: todosEquiposAgregados,
            onSelect: (selectedDuplicados) => {
              setModalDuplicados(prev => ({ ...prev, show: false }))
              resolve({ cancelled: false, nuevosData: nuevos, duplicadosData: selectedDuplicados })
            },
            onHide: () => {
              setModalDuplicados(prev => ({ ...prev, show: false }))
              resolve({ cancelled: true, nuevosData: [], duplicadosData: [] })
            },
          })
        })
      }
      return { cancelled: false, nuevosData: nuevos, duplicadosData: [] }
    } catch {
      Swal.fire('Error', 'No se pudo validar duplicados', 'error')
      return { cancelled: true, nuevosData: [], duplicadosData: [] }
    }
  }

  // ── Resolver ítems sin coincidencia (Excel) ───────────────────────
  const handleResolverItems = (equiposSinItem, callback) => {
    setModalResolverItems({
      show: true,
      data: equiposSinItem,
      onResolve: (resueltos) => {
        setModalResolverItems(prev => ({ ...prev, show: false }))
        callback(resueltos)
      },
      onHide: () => setModalResolverItems(prev => ({ ...prev, show: false })),
    })
  }

  // ── Agregar a lista pendiente ─────────────────────────────────────
  const agregarEquipos = (nuevos) => {
    const arr = Array.isArray(nuevos) ? nuevos : [nuevos]
    setPendingEquipos(prev => [...prev, ...arr])
  }

  // ── Editar equipo pendiente (estado local) ────────────────────────
  const editarEquipoPendiente = (equipoId, datos) => {
    setPendingEquipos(prev =>
      prev.map(e => e._id === equipoId ? { ...e, ...datos } : e)
    )
  }

  // ── Corregir equipo en BD y en estado local ───────────────────────
  const corregirEquipo = async (equipoId, datos) => {
    await actualizarDatos('Equipos', datos, equipoId)
    setPendingEquipos(prev =>
      prev.map(e => e._id === equipoId ? { ...e, ...datos } : e)
    )
  }

  // ── Guardar equipos en la OT ──────────────────────────────────────
  const handleGuardarEquipos = async () => {
    if (pendingEquipos.length === 0) return
    if (!motivoAdicion) {
      Alertassw('error', 'Seleccione el motivo de adición antes de guardar')
      return
    }
    setSavingEquipos(true)
    try {
      const payload = {
        equipos: pendingEquipos.map(e => ({ equipo: e._id, serial: e.serie })),
        comment: 'Adición desde OT en proceso',
        motivoAdicion,
        tipoAdicion: 'historial',
      }
      // Si el objeto tiene ot.ot es una Programacion (OtSelectMain); si no, es una OT directa (Otselect)
      const isProgramacion = Boolean(ot?.ot)
      if (isProgramacion) {
        await addEquiposProgramacion(ot._id, payload)
      } else {
        await addEquipments(ot._id, ot.numeroOrden, PARENT, payload)
      }
      setPendingEquipos([])
      Alertassw('ok', `${payload.equipos.length} equipo(s) agregado(s) correctamente`)
      setRefresh(!refresh)
      setEstadoModal(false)
    } catch (error) {
      if (error?.response?.data?.error?.message === 'EQUIPOS EXISTENTES') {
        const detalle = error.response.data.detail || ''
        Alertassw('error', 'Equipos ya existen en otra OT', detalle)
      } else if (error?.response?.data?.error?.message === 'DUPLICATED') {
        let msg = ''
        error.response.data.detail?.objects?.forEach(d => { msg += `• ${d.item?.nombre} - ${d.serie}\n` })
        Alertassw(
          'toast',
          error.response.data.detail?.message || 'Equipo duplicado en la OT',
          msg,
          'error',
          10000
        )
      } else {
        Alertassw('error', 'Error al agregar equipos', error?.response?.data?.error?.message || '')
      }
    } finally {
      setSavingEquipos(false)
    }
  }

  return (
    <>
      {/* Modal duplicados */}
      <ModalDuplicados
        show={modalDuplicados.show}
        data={modalDuplicados.data || { nuevos: [], duplicados: [] }}
        equiposYaAgregados={modalDuplicados.equiposYaAgregados}
        onSelect={modalDuplicados.onSelect || (() => {})}
        onHide={modalDuplicados.onHide || (() => {})}
      />

      {/* Modal resolver ítems (Excel) */}
      <ModalResolverItems
        show={modalResolverItems.show}
        equiposSinItem={modalResolverItems.data || []}
        onResolve={modalResolverItems.onResolve || (() => {})}
        onHide={modalResolverItems.onHide || (() => {})}
      />

      {/* Motivo de adición + Sede para nuevos equipos */}
      <Row className="g-2 mb-3 align-items-end">
        <Col md={4}>
          <Form.Label className="fw-semibold small mb-1">
            Motivo de adición <span className="text-danger">*</span>
          </Form.Label>
          <Form.Select
            size="sm"
            value={motivoAdicion}
            onChange={e => setMotivoAdicion(e.target.value)}
          >
            <option value="">— Seleccione un motivo —</option>
            <option value="Autorizado por el cliente">Autorizado por el cliente</option>
            <option value="Completar Cantidades">Completar Cantidades</option>
          </Form.Select>
        </Col>
        <Col md={5}>
          <Form.Label className="fw-semibold small mb-1">
            Sede para nuevos equipos
          </Form.Label>
          <Form.Select
            size="sm"
            value={sedeIdEnModal}
            onChange={e => setSedeIdEnModal(e.target.value)}
            disabled={cargandoSedes || clienteSedes.length === 0}
          >
            {cargandoSedes && <option value="">Cargando sedes...</option>}
            {!cargandoSedes && clienteSedes.length === 0 && (
              <option value="">El cliente no tiene sedes registradas</option>
            )}
            {!cargandoSedes && clienteSedes.map(s => (
              <option key={s._id} value={s._id}>
                {s.nameSede} {s.ciudad ? `— ${s.ciudad}` : ''}
              </option>
            ))}
          </Form.Select>
          <Form.Text className="text-muted">
            Aplica a equipos nuevos creados desde Excel o Manual.
          </Form.Text>
        </Col>
        <Col md={3} className="text-end">
          {/* Botón guardar */}
          {pendingEquipos.length > 0 && (
            <div className="d-flex justify-content-end mt-3">
              <Button
                variant="success"
                onClick={handleGuardarEquipos}
                disabled={savingEquipos}
              >
                {savingEquipos
                  ? <><BSSpinner size="sm" className="me-1" />Guardando...</>
                  : <><i className="bi bi-cloud-upload me-1" />Guardar {pendingEquipos.length} equipo{pendingEquipos.length !== 1 ? 's' : ''}</>
                }
              </Button>
            </div>
          )}
        </Col>
      </Row>

      {/* Lista de equipos pendientes */}
      {pendingEquipos.length > 0 && (
        <ListaEquiposConfirmacion
          equipos={pendingEquipos}
          onEliminar={(id) => setPendingEquipos(prev => prev.filter(e => e._id !== id))}
          onEditar={editarEquipoPendiente}
          listaSedes={sedes}
          onCorregir={corregirEquipo}
        />
      )}

      {/* EquipoManager (4 tabs: Manual, Excel, Existentes, Últimas Cal.) */}
      {clienteId && (
        <EquipoManager
          clienteId={clienteId}
          sedeId={sedeIdEnModal}
          onValidarDuplicados={validarDuplicados}
          onResolverItems={handleResolverItems}
          onAgregar={agregarEquipos}
          equiposYaAgregados={todosEquiposAgregados}
          listaSedes={clienteSedes.length > 0 ? clienteSedes : sedes}
          onCorregir={corregirEquipo}
          initialEquipos={initialEquipos}
          onEquiposLoaded={handleEquiposLoaded}
          initialDataUltimasCal={initialDataUltimasCal}
          onUltimasCalLoaded={handleUltimasCalLoaded}
        />
      )}

    </>
  )
}

export default AgregarEquiposOt
